import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { subscribeEmail } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { useLiveAnnounce, LiveRegion } from "@/lib/a11y";

export default function EmailSignup({ source = "homepage", dark = false }) {
    const [firstName, setFirstName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const { message: liveMessage, announce } = useLiveAnnounce();

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email) return;
        setLoading(true);
        try {
            await subscribeEmail({ email, first_name: firstName, source, consent: true });
            setDone(true);
            toast.success("Thank you — you're on the journey.");
            announce("Thank you. You are now on the journey.");
            setFirstName("");
            setEmail("");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            setError(msg);
            toast.error(msg);
            announce(msg);
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div
                data-testid={TID.home.signupSuccess}
                className={`p-8 border ${dark ? "border-gold/40 text-ivory" : "border-oxblood/30 text-charcoal"}`}
            >
                <p className="eyebrow text-gold mb-3">You're on the journey</p>
                <p className="font-serif italic text-2xl md:text-3xl leading-snug">
                    Thank you. Watch your inbox for the first letter.
                </p>
            </div>
        );
    }

    return (
        <form
            data-testid={TID.home.signupForm}
            onSubmit={onSubmit}
            className="space-y-6"
        >
            <LiveRegion message={liveMessage} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                    <label htmlFor={`fn-${source}`} className={`eyebrow ${dark ? "text-ivory/60" : "text-stone"}`}>
                        First name
                    </label>
                    <input
                        id={`fn-${source}`}
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={dark ? "input-line-light mt-2" : "input-line mt-2"}
                        placeholder="Your first name"
                        data-testid={TID.home.signupFirstName}
                    />
                </div>
                <div>
                    <label htmlFor={`em-${source}`} className={`eyebrow ${dark ? "text-ivory/60" : "text-stone"}`}>
                        Email address
                    </label>
                    <input
                        id={`em-${source}`}
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={dark ? "input-line-light mt-2" : "input-line mt-2"}
                        placeholder="you@somewhere.com"
                        data-testid={TID.home.signupEmail}
                    />
                </div>
            </div>

            {error && (
                <p data-testid={TID.home.signupError} className="text-sm text-oxblood">
                    {error}
                </p>
            )}

            <button
                type="submit"
                disabled={loading}
                data-testid={TID.home.signupSubmit}
                className={dark ? "btn-secondary-light w-full sm:w-auto" : "btn-primary w-full sm:w-auto"}
            >
                {loading ? "Subscribing…" : "Follow the Journey"}
                <ArrowRight size={16} strokeWidth={1.5} />
            </button>

            <p className={`text-xs ${dark ? "text-ivory/40" : "text-stone"}`}>
                By subscribing you consent to receive occasional letters from Chrissa. No tracking pixels.
                Unsubscribe at any time.
            </p>
        </form>
    );
}
